/**
 * Localization Policy — Single Source of Truth
 *
 * Maps entity types to language coverage and translatable field sets.
 * Editors, audit routines, and coverage reporting all read from here.
 * No other place should hardcode which entities get which languages.
 *
 * Policy summary (approved 2026-09-14):
 * - Topics + profile bios: all enabled locales, always.
 * - Publications: domestic language + English only (pre-translated).
 * - Everything else: JIT on demand, never pre-generated.
 */

import { limitForLocale, visibleTextLength } from './topicCompliance';

// ---------------------------------------------------------------------------
// Entity language coverage
// ---------------------------------------------------------------------------

export type EntityLanguageCoverage = 'all' | 'domestic_plus_en';

export type LocalizableEntity = 'topic' | 'publication' | 'profile';

/**
 * Which languages each entity type gets pre-translated into.
 * - `all`: every enabled locale in the system.
 * - `domestic_plus_en`: the entity's source language plus English.
 */
export const ENTITY_LANGUAGE_COVERAGE: Record<LocalizableEntity, EntityLanguageCoverage> = {
  topic: 'all',
  publication: 'domestic_plus_en',
  profile: 'all',
};

// ---------------------------------------------------------------------------
// Translatable field sets
// ---------------------------------------------------------------------------

/**
 * Fields that may be translated, per entity type.
 * Fields NOT listed here are forbidden (identity, credentials, etc.).
 */
export const TRANSLATABLE_FIELDS: Record<LocalizableEntity, string[]> = {
  topic: [
    'translated_name',
    'translated_slug',
    'translated_body',
    'translated_body_short',
    'meta_title',
    'meta_description',
  ],
  publication: [
    'translated_name',
    'translated_slug',
    'translated_body_short',
    'meta_title',
    'meta_description',
    // translated_body is NOT here — publication body is JIT-only, never pre-translated
  ],
  profile: [
    'translated_body',      // bio
    'meta_title',
    'meta_description',
    // name, slug, credentials, firm — never translated
  ],
};

/**
 * Check whether a field is translatable for a given entity type.
 * Use this in editors and validation — never hardcode field lists elsewhere.
 */
export function isFieldTranslatable(entity: LocalizableEntity, field: string): boolean {
  return TRANSLATABLE_FIELDS[entity].includes(field);
}

// ---------------------------------------------------------------------------
// "Fully written" check — the gate for indexing, hreflang, and sitemap
// ---------------------------------------------------------------------------

/**
 * Minimum fields that must be non-empty for a topic language to be
 * considered "fully written" (indexable, hreflang-eligible).
 */
export const TOPIC_WRITTEN_FIELDS = [
  'translated_name',
  'translated_body',
  'meta_title',
  'meta_description',
] as const;

/**
 * Minimum fields for a profile language.
 */
export const PROFILE_WRITTEN_FIELDS = [
  'translated_body',
] as const;

/**
 * Minimum fields for a publication language (domestic + EN only).
 * Body is never required — only title, excerpt, and metadata.
 */
export const PUBLICATION_WRITTEN_FIELDS = [
  'translated_name',
  'translated_body_short',
] as const;

/**
 * Check whether a localization_metadata row passes the "fully written" gate.
 *
 * @param entity - The entity type
 * @param row - The localization_metadata row (or null if no row exists)
 * @returns true if the row has all required fields filled
 */
export function isFullyWritten(
  entity: LocalizableEntity,
  row: Record<string, unknown> | null | undefined,
): boolean {
  if (!row) return false;

  const requiredFields =
    entity === 'topic'
      ? TOPIC_WRITTEN_FIELDS
      : entity === 'profile'
        ? PROFILE_WRITTEN_FIELDS
        : PUBLICATION_WRITTEN_FIELDS;

  const present = requiredFields.every((field) => {
    const value = row[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
  if (!present) return false;

  // Topics carry the search-engine length rules too: a title or description
  // outside the window is a thin page, so it is not indexable.
  if (entity === 'topic') {
    // Each language is judged by its own window: a correct Chinese title is far
    // shorter than a correct German one.
    const rowLocale = typeof row.locale === 'string' && row.locale ? row.locale : 'en';
    const withinLimit = (value: unknown, field: 'name' | 'meta_title' | 'meta_description'): boolean => {
      const text = typeof value === 'string' ? value.trim() : '';
      const rule = limitForLocale(rowLocale, field);
      const length = visibleTextLength(text);
      return length >= rule.min && length <= rule.max;
    };
    if (!withinLimit(row.translated_name, 'name')) return false;
    if (!withinLimit(row.meta_title, 'meta_title')) return false;
    if (!withinLimit(row.meta_description, 'meta_description')) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Indexing decisions
// ---------------------------------------------------------------------------

export interface IndexingDecision {
  /** Whether the page should be indexable by search engines */
  indexable: boolean;
  /** Whether to include this language in hreflang alternate links */
  includeInHreflang: boolean;
  /** Whether to include in sitemap */
  includeInSitemap: boolean;
  /** Whether to redirect to the source language instead of serving */
  redirectToSource: boolean;
}

/**
 * Determine the indexing treatment for a localized entity page.
 *
 * Rules (from the approved plan):
 * - Fully written → indexable, hreflang, sitemap.
 * - Partially written (some fields present) → served but noindex, no hreflang, no sitemap.
 * - Empty (no row or name-only) → redirect to source language, noindex.
 */
export function getIndexingDecision(
  entity: LocalizableEntity,
  row: Record<string, unknown> | null | undefined,
  isSourceLocale: boolean,
): IndexingDecision {
  // Source locale is always indexable (it's the original)
  if (isSourceLocale) {
    return { indexable: true, includeInHreflang: true, includeInSitemap: true, redirectToSource: false };
  }

  // No row at all → redirect to source
  if (!row) {
    return { indexable: false, includeInHreflang: false, includeInSitemap: false, redirectToSource: true };
  }

  // Check if fully written
  if (isFullyWritten(entity, row)) {
    return { indexable: true, includeInHreflang: true, includeInSitemap: true, redirectToSource: false };
  }

  // Check if completely empty (name-only or nothing useful)
  const hasAnyContent = ['translated_name', 'translated_body', 'meta_title', 'meta_description']
    .some((f) => {
      const v = row[f];
      return typeof v === 'string' && v.trim().length > 0;
    });

  if (!hasAnyContent) {
    return { indexable: false, includeInHreflang: false, includeInSitemap: false, redirectToSource: true };
  }

  // Partially written → served but noindex
  return { indexable: false, includeInHreflang: false, includeInSitemap: false, redirectToSource: false };
}
